import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { getEnabledServices } from "../../../utils/sessionDB";
import { pickTicketRemarks } from "../../../services/ticketTrackerService";

export const TICKET_PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
export const TICKET_ASSIGNEE_OPTIONS = [
  "Aditya", "Anagha", "Pratham", 
  "Ramesh", "Ravi", "Vatsa", "Vaibhav", "Vinutha"
];

export const REQUEST_TYPE_OPTIONS = ["Bug", "Enhancement", "Development", "PRD_Discussion","Initiatives"];

export const TICKET_STATUS_OPTIONS = [
  "Open",
  "In_Progress",
  "Testing",
  "Resolved",
  "Closed",
  "Rejected",
  "Waiting",
  "Duplicate",
];

export const EMPTY_TICKET_FORM = {
  service: "",
  module: "",
  subModule: "",
  title: "",
  priority: "",
  requestType: "",
  description: "",
  assignee: "",
  targetRelease: "",
  progressPercentage: "",
  remarks: "",
  createdBy: "",
  status: "Open",
};

export const ticketInputClass =
  "w-full h-8 rounded-lg border border-(--color-text-border) px-2.5 text-xs text-foreground bg-white focus:border-accent focus:outline-hidden focus:ring-1 focus:ring-accent/15 transition";
export const ticketInputErrorClass = "border-error focus:border-error";
export const ticketLabelClass = "block text-[11px] font-semibold per-file-hint mb-1";
export const ticketTextareaClass =
  "w-full min-h-[72px] rounded-lg border border-(--color-text-border) px-3 py-2 text-xs text-foreground bg-white focus:border-accent focus:outline-hidden focus:ring-1 focus:ring-accent/15 transition resize-y";

export function ensureOption(names, value) {
  const list = [...names];
  if (value && !list.includes(value)) list.unshift(value);
  return list;
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-0.5 text-[10px] text-error">{message}</p>;
}

/**
 * Shared ticket form fields for create (landing) and editable detail.
 * Group related fields into responsive rows with read-only ticket metadata.
 * mode: "create" | "edit"
 */
export function TicketFormFields({
  form,
  updateField,
  fieldErrors = {},
  mode = "create",
  showRequestId = false,
  requestId,
  readOnly = false,
}) {
  const enabledServices = getEnabledServices();
  const withCurrentValue = (options, value) => mode === "edit" ? ensureOption(options, value) : options;
  const serviceOptions = withCurrentValue(Object.keys(enabledServices), form.service);
  const moduleOptions = withCurrentValue(Object.keys(enabledServices[form.service] || {}), form.module);
  const subModuleOptions = withCurrentValue(
    Object.keys(enabledServices[form.service]?.[form.module] || {}), form.subModule
  );
  const requestTypeOptions = ensureOption(REQUEST_TYPE_OPTIONS, form.requestType);
  const statusOptions = ensureOption(TICKET_STATUS_OPTIONS, form.status);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Service <span className="text-error">*</span>
        </span>
        <Select disabled={readOnly} value={form.service || undefined} onValueChange={(v) => updateField("service", v)}>
          <SelectTrigger
            className={`${ticketInputClass} ${fieldErrors.service ? ticketInputErrorClass : ""}`}
          >
            <SelectValue placeholder="Service" />
          </SelectTrigger>
          <SelectContent>
            {serviceOptions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.service} />
      </label>
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Module <span className="text-error">*</span>
        </span>
        <Select
          value={form.module || undefined}
          onValueChange={(v) => updateField("module", v)}
          disabled={readOnly || !form.service}
        >
          <SelectTrigger
            className={`${ticketInputClass} ${fieldErrors.module ? ticketInputErrorClass : ""}`}
          >
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent>
            {moduleOptions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.module} />
      </label>
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Sub Module <span className="text-error">*</span>
        </span>
        <Select
          value={form.subModule || undefined}
          onValueChange={(v) => updateField("subModule", v)}
          disabled={readOnly || !form.module}
        >
          <SelectTrigger
            className={`${ticketInputClass} ${fieldErrors.subModule ? ticketInputErrorClass : ""}`}
          >
            <SelectValue placeholder="Sub Module" />
          </SelectTrigger>
          <SelectContent>
            {subModuleOptions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.subModule} />
      </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Title <span className="text-error">*</span>
        </span>
        <input
          readOnly={readOnly}
          type="text"
          value={form.title}
          onChange={(e) => updateField("title", e.target.value)}
          className={`${ticketInputClass} ${fieldErrors.title ? ticketInputErrorClass : ""}`}
          placeholder="Title"
        />
        <FieldError message={fieldErrors.title} />
      </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Description <span className="text-error">*</span>
        </span>
        <textarea
          readOnly={readOnly}
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
          className={`${ticketTextareaClass} ${fieldErrors.description ? ticketInputErrorClass : ""}`}
          placeholder="Description"
          rows={3}
        />
        <FieldError message={fieldErrors.description} />
      </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Priority <span className="text-error">*</span>
        </span>
        <Select disabled={readOnly} value={form.priority || undefined} onValueChange={(value) => updateField("priority", value)}>
          <SelectTrigger className={[ticketInputClass, fieldErrors.priority ? ticketInputErrorClass : ""].join(" ")}>
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            {TICKET_PRIORITY_OPTIONS.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.priority} />
      </label>
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Request Type <span className="text-error">*</span>
        </span>
        <Select
          disabled={readOnly}
          value={form.requestType || undefined}
          onValueChange={(v) => updateField("requestType", v)}
        >
          <SelectTrigger
            className={`${ticketInputClass} ${fieldErrors.requestType ? ticketInputErrorClass : ""}`}
          >
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {requestTypeOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.requestType} />
      </label>
        <dl className="min-w-0 text-xs">
          <dt className={ticketLabelClass}>Created By</dt>
          <dd className="m-0 flex min-h-8 items-center wrap-break-word text-foreground">{form.createdBy || "Unavailable"}</dd>
          <FieldError message={fieldErrors.createdBy} />
        </dl>
        {showRequestId ? (
          <dl className="min-w-0 text-xs">
            <dt className={ticketLabelClass}>Request ID</dt>
            <dd className="m-0 flex min-h-8 items-center wrap-break-word text-foreground">{requestId ?? "Unavailable"}</dd>
          </dl>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>Status</span>
        <Select
          disabled={readOnly}
          value={form.status || undefined}
          onValueChange={(v) => updateField("status", v)}
        >
          <SelectTrigger className={ticketInputClass}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt.replaceAll("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>Target Date</span>
        <input
          readOnly={readOnly}
          type="text"
          value={form.targetRelease}
          onChange={(e) => updateField("targetRelease", e.target.value)}
          className={ticketInputClass}
          placeholder="Target Date"
        />
      </label>
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>
          Assignee <span className="text-error">*</span>
        </span>
        <Select disabled={readOnly} value={form.assignee || undefined} onValueChange={(value) => updateField("assignee", value)}>
          <SelectTrigger className={[ticketInputClass, fieldErrors.assignee ? ticketInputErrorClass : ""].join(" ")}>
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            {TICKET_ASSIGNEE_OPTIONS.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors.assignee} />
      </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
      <label className="flex min-w-0 flex-col">
        <span className={ticketLabelClass}>Remarks</span>
        <textarea
          readOnly={readOnly}
          value={form.remarks}
          onChange={(e) => updateField("remarks", e.target.value)}
          className={ticketTextareaClass}
          placeholder="Remarks"
          rows={3}
        />
      </label>
      </div>
    </div>
  );
}

export function validateTicketForm(form) {
  const errors = {};
  if (!form.service?.trim()) errors.service = "Service is required.";
  if (!form.module?.trim()) errors.module = "Module is required.";
  if (!form.subModule?.trim()) errors.subModule = "Sub Module is required.";
  if (!form.title?.trim()) errors.title = "Title is required.";
  if (!form.priority?.trim()) errors.priority = "Priority is required.";
  else if (!TICKET_PRIORITY_OPTIONS.includes(form.priority)) errors.priority = "Select a valid priority.";
  if (!form.requestType?.trim()) errors.requestType = "Request Type is required.";
  if (!form.description?.trim()) errors.description = "Description is required.";
  if (!form.assignee?.trim()) errors.assignee = "Assignee is required.";
  else if (!TICKET_ASSIGNEE_OPTIONS.includes(form.assignee)) errors.assignee = "Select a valid assignee.";
  if (!form.createdBy?.trim()) errors.createdBy = "Created By is required.";
  return errors;
}

export function mapTicketToForm(ticket, fallbackCreatedBy = "") {
  return {
    service: ticket?.service || "",
    module: ticket?.module || "",
    subModule: ticket?.subModule || ticket?.sub_module || "",
    title: ticket?.title || "",
    priority: ticket?.priority || "",
    requestType: ticket?.requestType || ticket?.request_type || "",
    description: ticket?.description || ticket?.desc || "",
    assignee:
      ticket?.assignee != null
        ? String(ticket.assignee)
        : ticket?.assigneeName != null
          ? String(ticket.assigneeName)
          : "",
    targetRelease: ticket?.targetRelease || ticket?.target_release || "",
    progressPercentage: String(ticket?.progressPercentage ?? ""),
    remarks: pickTicketRemarks(ticket),
    createdBy:
      ticket?.createdBy != null
        ? String(ticket.createdBy)
        : ticket?.createdByName != null
          ? String(ticket.createdByName)
          : String(fallbackCreatedBy || ""),
    status: ticket?.status || "Open",
  };
}

export function TicketProgressField({ form, updateField, readOnly = false }) {
  return (
    <label className="flex w-36 min-w-0 flex-col">
      <span className="sr-only">Percentage Completed</span>
      <input type="text" value={form.progressPercentage}
        onChange={(e) => updateField("progressPercentage", e.target.value)}
        readOnly={readOnly} className={ticketInputClass} placeholder="Progress percentage" />
    </label>
  );
}

export function formatTicketProgress(value) {
  if (value == null || String(value).trim() === "") return "Unavailable";
  const text = String(value).trim();
  return text.endsWith("%") ? text : text + "%";
}
